import * as React from "react";
import { Avatar, Button, Sheet, SheetContent, SheetHeader, SheetTitle, Skeleton } from "@/components/ui";
import { formatRelativeTime } from "@/components/reviews";
import { ActorRef, DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { DesignPackageActivityEvent } from "@/services/bedrock/dtos/DesignPackageActivity";
import { useActivity } from "../hooks/useActivity";
import { useDesignPackage } from "../hooks/useDesignPackage";
import { ACTIVITY_LABELS, asUser } from "../thread";
import { initials } from "./PresenceAvatars";

interface ActivityPanelProps {
  packageId: number;
  variant: "inline" | "sheet";
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
}

export const ActorAvatar: React.FC<{ actor: ActorRef }> = ({ actor }) => (
  <Avatar size="sm">
    {actor.avatarUrl && <Avatar.Image src={actor.avatarUrl} alt={actor.name} />}
    <Avatar.Fallback>{initials(actor.name)}</Avatar.Fallback>
  </Avatar>
);

export function activityDetails(
  event: Pick<DesignPackageActivityEvent, "action" | "payload" | "designPackageItemId">,
  pkg: DesignPackageDetail | undefined
): string | null {
  const payload = event.payload as Record<string, unknown>;
  const slide = () => {
    const itemId = (payload.designPackageItemId as number | undefined) ?? event.designPackageItemId;
    const position = pkg?.items.find(i => i.id === itemId)?.position;
    return position != null ? `Slide ${position}` : null;
  };

  switch (event.action) {
    case "refined":
    case "html_edited":
      return typeof payload.instructions === "string" && payload.instructions ? `“${payload.instructions}”` : null;
    case "renamed":
      return `${payload.from} → ${payload.to}`;
    case "published":
    case "prompt_updated":
      return typeof payload.title === "string" ? payload.title : null;
    case "unpublished": {
      const removed = Number(payload.deletedScheduledPosts ?? 0);
      return removed > 0 ? `Removed ${removed} scheduled posts` : null;
    }
    case "rendered_for_player":
      return typeof payload.playerName === "string" ? payload.playerName : null;
    case "background_default_changed":
    case "backgrounds_generated":
    case "background_uploaded":
    case "background_attached":
    case "background_removed":
      return slide();
    default:
      return null;
  }
}

const ActivityList: React.FC<{ packageId: number }> = ({ packageId }) => {
  const { eventsNewestFirst, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useActivity(packageId);
  const { pkg } = useDesignPackage(packageId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map(i => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-3" data-testid="activity-list">
      {eventsNewestFirst.map(event => {
        const details = activityDetails(event, pkg);
        return (
          <li key={event.id} className="flex gap-2.5" data-testid="activity-row">
            <ActorAvatar actor={event.actor} />
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="text-sm text-slate-700">
                <span className="font-semibold">{event.actor.name}</span>
                {event.actor.onBehalfOf && (
                  <span className="text-slate-500"> {asUser(event.actor.onBehalfOf.name)}</span>
                )}
                <span> · {ACTIVITY_LABELS[event.action]}</span>
              </p>
              {details && <p className="truncate text-xs text-slate-500">{details}</p>}
              <p className="text-xs text-slate-400">{formatRelativeTime(event.createdAt)}</p>
            </div>
          </li>
        );
      })}
      {hasNextPage && (
        <li>
          <Button variant="ghost" size="sm" loading={isFetchingNextPage} onClick={() => void fetchNextPage()}>
            Load earlier changes
          </Button>
        </li>
      )}
    </ol>
  );
};

export const ActivityPanel: React.FC<ActivityPanelProps> = ({ packageId, variant, open, onOpenChange }) => {
  if (variant === "inline") return <ActivityList packageId={packageId} />;
  return (
    <Sheet open={open} onOpenChange={next => onOpenChange?.(next)}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Activity</SheetTitle>
        </SheetHeader>
        <div className="px-4 pb-4">
          <ActivityList packageId={packageId} />
        </div>
      </SheetContent>
    </Sheet>
  );
};
